import React, { createContext, useState, useCallback, ReactNode } from 'react';
import { Snackbar, Alert, AlertProps, Button, IconButton, type SxProps, type Theme } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

export type NotificationSeverity = 'success' | 'error' | 'warning' | 'info';

export interface NotificationOptions {
  severity?: NotificationSeverity;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  persist?: boolean;
  /** Extra styles for the Snackbar (e.g. a bottom offset to clear a sticky footer). */
  sx?: SxProps<Theme>;
}

interface Notification {
  id: string;
  message: string;
  severity: NotificationSeverity;
  duration: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  sx?: SxProps<Theme>;
}

interface NotificationContextType {
  showNotification: (message: string, options?: NotificationOptions) => void;
  showSuccess: (message: string, options?: Omit<NotificationOptions, 'severity'>) => void;
  showError: (message: string, options?: Omit<NotificationOptions, 'severity'>) => void;
  showWarning: (message: string, options?: Omit<NotificationOptions, 'severity'>) => void;
  showInfo: (message: string, options?: Omit<NotificationOptions, 'severity'>) => void;
}

export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [, setNotifications] = useState<Notification[]>([]);
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);

  const showNotification = useCallback((message: string, options?: NotificationOptions) => {
    const notification: Notification = {
      id: `${Date.now()}-${Math.random()}`,
      message,
      severity: options?.severity || 'info',
      duration: options?.persist ? 0 : (options?.duration ?? 5000),
      action: options?.action,
      sx: options?.sx,
    };

    setNotifications((prev) => [...prev, notification]);

    // If no current notification, show this one immediately
    setCurrentNotification((current) => (current ? current : notification));
  }, []);

  const showSuccess = useCallback(
    (message: string, options?: Omit<NotificationOptions, 'severity'>) => {
      showNotification(message, { ...options, severity: 'success' });
    },
    [showNotification]
  );

  const showError = useCallback(
    (message: string, options?: Omit<NotificationOptions, 'severity'>) => {
      showNotification(message, { ...options, severity: 'error' });
    },
    [showNotification]
  );

  const showWarning = useCallback(
    (message: string, options?: Omit<NotificationOptions, 'severity'>) => {
      showNotification(message, { ...options, severity: 'warning' });
    },
    [showNotification]
  );

  const showInfo = useCallback(
    (message: string, options?: Omit<NotificationOptions, 'severity'>) => {
      showNotification(message, { ...options, severity: 'info' });
    },
    [showNotification]
  );

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setCurrentNotification(null);

    // After closing, check if there are more notifications to show
    setTimeout(() => {
      setNotifications((prev) => {
        const remaining = prev.filter((n) => n.id !== currentNotification?.id);
        if (remaining.length > 0) {
          setCurrentNotification(remaining[0]);
          return remaining.slice(1);
        }
        return [];
      });
    }, 100);
  };

  const getSeverityProps = (severity: NotificationSeverity): Partial<AlertProps> => {
    switch (severity) {
      case 'success':
        return { severity: 'success' };
      case 'error':
        return { severity: 'error' };
      case 'warning':
        return { severity: 'warning' };
      case 'info':
      default:
        return { severity: 'info' };
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
      }}
    >
      {children}
      {currentNotification && (
        <Snackbar
          open={true}
          autoHideDuration={currentNotification.duration || null}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={currentNotification.sx}
        >
          <Alert
            onClose={handleClose}
            {...getSeverityProps(currentNotification.severity)}
            sx={{
              width: '100%',
              minWidth: 300,
              backgroundColor: (theme) => {
                const severity = currentNotification.severity;
                switch (severity) {
                  case 'success':
                    return theme.palette.success.dark;
                  case 'error':
                    return theme.palette.error.dark;
                  case 'warning':
                    return theme.palette.warning.dark;
                  case 'info':
                  default:
                    return theme.palette.info.dark;
                }
              },
              color: '#fff !important',
              '& .MuiAlert-icon': {
                color: '#fff !important',
              },
              '& .MuiAlert-message': {
                color: '#fff !important',
              },
            }}
            action={
              <>
                {currentNotification.action && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      currentNotification.action?.onClick();
                      handleClose();
                    }}
                  >
                    {currentNotification.action.label}
                  </Button>
                )}
                <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            {currentNotification.message}
          </Alert>
        </Snackbar>
      )}
    </NotificationContext.Provider>
  );
}
